import React from 'react'
import { View } from 'react-native'

import { twMerge } from '../../tailwind/merge'

export const PUNCTUALITY_SEVERITY_BAR_STATUSES = [
    'success',
    'warning',
    'critical',
    'neutral',
] as const

export type PunctualitySeverityBarStatus = (typeof PUNCTUALITY_SEVERITY_BAR_STATUSES)[number]

export type PunctualitySeverityBarThresholds = {
    warningBelowPercent: number | null
    alertBelowPercent: number | null
}

export type PunctualitySeverityBarProps = {
    value: number | null
    status: PunctualitySeverityBarStatus
    thresholds?: PunctualitySeverityBarThresholds | null
    showThresholds?: boolean
    className?: string
    testID?: string
}

export type PunctualitySeverityBarSegment = {
    from: number
    to: number
    filledPercent: number
}

const SCALE_MIN = 0
const SCALE_MAX = 100

export const SEVERITY_BAR_OUTLINE: Record<PunctualitySeverityBarStatus, string> = {
    success: 'border-status-success',
    warning: 'border-status-warning',
    critical: 'border-status-critical',
    neutral: 'border-status-neutral',
}

export const SEVERITY_BAR_FILL: Record<PunctualitySeverityBarStatus, string> = {
    success: 'bg-status-success',
    warning: 'bg-status-warning',
    critical: 'bg-status-critical',
    neutral: 'bg-status-neutral',
}

const clampToScale = (value: number): number => Math.min(SCALE_MAX, Math.max(SCALE_MIN, value))

const isCut = (threshold: number | null): threshold is number =>
    threshold !== null &&
    Number.isFinite(threshold) &&
    threshold > SCALE_MIN &&
    threshold < SCALE_MAX

export const severityBarCuts = (
    thresholds: PunctualitySeverityBarThresholds | null | undefined,
): number[] => {
    if (!thresholds) {
        return []
    }
    const configured = [thresholds.alertBelowPercent, thresholds.warningBelowPercent].filter(isCut)
    return [...new Set(configured)].sort((a, b) => a - b)
}

/**
 * The segments the bar draws: the 0–100 scale split at every configured
 * threshold, each with the share of it the value fills. A threshold of 0 or
 * null is a band that is not configured, so it cuts nothing; no value fills nothing.
 */
export const planSeverityBarSegments = (
    value: number | null,
    cuts: readonly number[],
): PunctualitySeverityBarSegment[] => {
    const filled = value === null || !Number.isFinite(value) ? SCALE_MIN : clampToScale(value)
    const edges = [SCALE_MIN, ...cuts, SCALE_MAX]
    return edges.slice(1).map((to, index) => {
        const from = edges[index]
        const share = Math.min(1, Math.max(0, (filled - from) / (to - from)))
        return { from, to, filledPercent: share * 100 }
    })
}

/**
 * Figma `Widget/Stat/SeverityBar` (18896:99719): a value on the 0–100 scale in
 * the colour of its status, cut at the widget's thresholds (PUN-KPI-MK10).
 * Neutral is filled to the value too (B-139). Contract-less by design decision
 * D4 (2026-10-29): the Figma track ratio and threshold mask are Figma-only, so
 * the inks, the outline and the height are asserted against the stored dump.
 */
export const PunctualitySeverityBar = ({
    value,
    status,
    thresholds,
    showThresholds = true,
    className,
    testID = 'punctuality-severity-bar',
}: PunctualitySeverityBarProps) => {
    const cuts = showThresholds ? severityBarCuts(thresholds) : []
    const segments = planSeverityBarSegments(value, cuts)
    const last = segments.length - 1
    return (
        <View className={twMerge('h-[0.5rem] w-full flex-row gap-2xs', className)} testID={testID}>
            {segments.map((segment, index) => (
                <View
                    key={segment.from}
                    testID={`${testID}-segment`}
                    style={{ flexGrow: segment.to - segment.from, flexBasis: 0 }}
                    className={twMerge(
                        'h-full overflow-hidden border-y-2 border-solid',
                        index === 0 && 'border-l-2',
                        index === last && 'border-r-2',
                        SEVERITY_BAR_OUTLINE[status],
                    )}
                >
                    {segment.filledPercent > 0 && (
                        <View
                            testID={`${testID}-fill`}
                            style={{ width: `${segment.filledPercent}%` }}
                            className={twMerge('h-full', SEVERITY_BAR_FILL[status])}
                        />
                    )}
                </View>
            ))}
        </View>
    )
}

PunctualitySeverityBar.displayName = 'PunctualitySeverityBar'
